import React from 'react';
import _ from 'lodash';
import Xarrow from 'react-xarrows/lib';
import {
  composeDeviceConnectionId,
  deviceActions,
  devices as deviceTypes,
  getDeviceInSnapInPlacement,
  PressProfiles
} from '../../util/devices';
import {
  ConnectionConfig,
  Device,
  Dock,
  IInputDevice,
  Scene,
  SCENE_ACTION
} from './types';
import { IColor } from '../ColorSlider/ColorSlider';
import DevicesConnection from '../../util/DevicesConnection';

export const getInputDevices = (devices: Device[]) =>
  devices.filter(device => !!Object.keys(device.events).length);
export const getOutputDevices = (devices: Device[]) =>
  devices.filter(device => !!Object.keys(device.actions).length);
export const getDeviceIndex = (devices: Device[], device: Device) =>
  devices.findIndex(dev => dev.id === device.id);
export const getSceneIndex = (scenes: Scene[], scene: Scene) =>
  scenes.findIndex(sc => sc.id === scene.id);

export const isDeviceActive = (
  connectionsConfiguration,
  inputDevice: IInputDevice,
  device: Device,
  dock: Dock,
  configDirection: string,
  pressProfile: PressProfiles
) => {
  if (!inputDevice) {
    return false;
  }

  const { startsFrom, id } = inputDevice;
  const deviceIndex = getDeviceIndex(dock.devices, device);

  if (startsFrom === configDirection) {
    return (
      id ===
      composeDeviceConnectionId(deviceIndex, dock.number, configDirection)
    );
  }
  const sourceDockFilterFunction =
    startsFrom === 'input'
      ? connection =>
          connection.sourceDockNumber ===
            inputDevice.connectionInputConfig.sourceDockNumber &&
          connection.sourceDeviceNumber ===
            inputDevice.connectionInputConfig.sourceDeviceNumber
      : connection =>
          connection.sourceDockNumber === dock.number &&
          connection.sourceDeviceNumber === deviceIndex;
  const targetDockFilterFunction =
    startsFrom === 'input'
      ? connection =>
          connection.targetDockNumber === dock.number &&
          connection.targetDeviceNumber === deviceIndex &&
          connection.triggerEvent === pressProfile
      : connection =>
          connection.targetDockNumber ===
            inputDevice.connectionInputConfig.targetDockNumber &&
          connection.targetDeviceNumber ===
            inputDevice.connectionInputConfig.targetDeviceNumber &&
          connection.triggerEvent === pressProfile;

  return (
    connectionsConfiguration
      .filter(({ connection }) => sourceDockFilterFunction(connection))
      .filter(({ connection }) => targetDockFilterFunction(connection)).length >
    0
  );
};

export const isInputButtonDisabled = (
  device: any,
  dock: Dock,
  inputDevice: IInputDevice,
  connectionsConfiguration: any[],
  deviceIndex: number,
  pressProfile: string
): boolean => {
  const connectionFromSceneFilter =
    inputDevice &&
    inputDevice.startsFrom === 'output' &&
    inputDevice.connectionInputConfig.targetAction === SCENE_ACTION &&
    // Checking if existing input to scene connection id different
    !_.isEmpty(
      connectionsConfiguration.find(
        ({ connection }) =>
          connection.sourceDeviceNumber === deviceIndex &&
          connection.sourceDockNumber === dock.number &&
          connection.triggerEvent === pressProfile &&
          connection.targetActionParameters.SceneId !== inputDevice.id
      )
    );
  const connectionFromOutputDeviceFilter =
    inputDevice &&
    inputDevice.startsFrom === 'output' &&
    inputDevice.connectionInputConfig.targetAction !== SCENE_ACTION &&
    !_.isEmpty(
      connectionsConfiguration.find(
        ({ connection }) =>
          connection.sourceDeviceNumber === deviceIndex &&
          connection.sourceDockNumber === dock.number &&
          connection.targetAction === SCENE_ACTION &&
          connection.triggerEvent === pressProfile
      )
    );
  return (
    device.symbol === deviceTypes.smokeDetector.symbol ||
    (inputDevice &&
      inputDevice.startsFrom === 'output' &&
      ((inputDevice.symbol === deviceTypes.frameLed.symbol &&
        device.symbol !== deviceTypes.frameButton.symbol) ||
        (device.symbol === deviceTypes.frameButton.symbol &&
          inputDevice.symbol !== deviceTypes.frameLed.symbol))) ||
    connectionFromSceneFilter ||
    connectionFromOutputDeviceFilter
  );
};

export const deviceDisabledBasedOnSceneConnection = (
  connectionsConfiguration,
  inputDevice?: IInputDevice
) => {
  if (!inputDevice) {
    return false;
  }

  const sceneConnection = connectionsConfiguration.find(
    ({ connection }) =>
      connection.sourceDeviceNumber ===
        inputDevice.connectionInputConfig.sourceDeviceNumber &&
      connection.sourceDockNumber ===
        inputDevice.connectionInputConfig.sourceDockNumber &&
      connection.triggerEvent ===
        inputDevice.connectionInputConfig.triggerEvent &&
      connection.sourceDockNumber === connection.targetDockNumber &&
      connection.targetAction === SCENE_ACTION
  );
  return sceneConnection;
};

export const sceneDisabledBasedOnSceneConnection = (
  connectionsConfiguration,
  pressProfile: PressProfiles,
  inputDevice?: IInputDevice,
  scene?: Scene
) => {
  if (
    !inputDevice ||
    inputDevice.connectionInputConfig.targetAction === SCENE_ACTION
  ) {
    return false;
  }
  const isInput = inputDevice && inputDevice.startsFrom === 'input';
  const deviceConnection = connectionsConfiguration.find(
    ({ connection }) =>
      connection.targetAction !== SCENE_ACTION ||
      (connection.targetAction === SCENE_ACTION &&
        connection.targetActionParameters.SceneId !== scene.id)
  );
  const inputHasDeviceConnections =
    connectionsConfiguration.find(
      ({ connection }) =>
        connection.sourceDockNumber ===
          inputDevice.connectionInputConfig.sourceDockNumber &&
        connection.sourceDeviceNumber ===
          inputDevice.connectionInputConfig.sourceDeviceNumber &&
        connection.targetAction !== SCENE_ACTION &&
        connection.targetDeviceNumber !== connection.sourceDeviceNumber &&
        connection.triggerEvent === pressProfile
    ) || inputDevice.symbol === deviceTypes.frameButton.symbol;
  return isInput ? inputHasDeviceConnections : deviceConnection;
};

export const isSceneActive = (
  connectionsConfiguration,
  inputDevice: IInputDevice,
  scene: Scene,
  pressProfile: PressProfiles
) => {
  if (!inputDevice) {
    return false;
  }
  const isInput = inputDevice && inputDevice.startsFrom === 'input';
  const inputConnectionsFilter = !_.isEmpty(
    connectionsConfiguration.find(
      ({ connection }) =>
        connection.targetAction === SCENE_ACTION &&
        connection.triggerEvent === pressProfile &&
        connection.targetActionParameters.SceneId === scene.id &&
        connection.sourceDeviceNumber ===
          inputDevice.connectionInputConfig.sourceDeviceNumber &&
        connection.sourceDockNumber ===
          inputDevice.connectionInputConfig.sourceDockNumber
    )
  );
  return isInput ? inputConnectionsFilter : inputDevice.id === scene.id;
};

export const hasInputDeviceConnections = (device, dock, connections) =>
  connections.filter(
    ({ connection }) =>
      connection.sourceDockNumber === dock.number &&
      connection.sourceDeviceNumber === getDeviceIndex(dock.devices, device)
  ).length > 0;

export const isElementPresent = elementId =>
  document.querySelector(`#${elementId}`);

const calculateDeviceMasks = (connectionConfig, dock, deviceType = 'input') => {
  const {
    sourceDeviceNumber,
    targetDeviceNumber,
    sourceDockNumber,
    targetDockNumber
  } = connectionConfig;
  const { number, snapInCode } = dock;
  const deviceNumber =
    deviceType === 'input'
      ? parseInt(sourceDeviceNumber, 10)
      : parseInt(targetDeviceNumber, 10);
  const dockNumber =
    deviceType === 'input' ? sourceDockNumber : targetDockNumber;
  const classes = [];

  if (dockNumber !== number) {
    return classes;
  }

  const activeDeviceMaskPlacement = getDeviceInSnapInPlacement(
    snapInCode,
    deviceNumber
  );
  if (!activeDeviceMaskPlacement) {
    return classes;
  }

  const {
    doubleDeviceSide,
    snapInHoleNumber,
    deviceShape
  } = activeDeviceMaskPlacement;

  // first device is always frame button
  if (deviceNumber === 0) {
    classes.push(`device-mask--frame-button`);
    // second device is always frame led
  } else if (deviceNumber === 1) {
    classes.push(`device-mask--frame-led`);
  } else if (deviceShape === 'round') {
    classes.push(`device-mask--rounded`);
    // if exists device for hole with index 4 (fourth hole) it will be always buzzer mount in into frame
  } else if (parseInt(snapInHoleNumber, 10) === 3) {
    classes.push(`device-mask--frame-buzzer`);
    // check if device is part of double switch
  } else if (!doubleDeviceSide.length) {
    classes.push(`device-mask--active-${snapInHoleNumber}`);
    // otherwise is single device
  } else {
    const quarter = doubleDeviceSide === 'left' ? 0 : 1;
    const quarterNumber = parseInt(snapInHoleNumber, 10) * 2 + quarter;

    classes.push(`device-mask--active-quarter-${quarterNumber}`);
  }

  return classes;
};
export const getActiveDeviceMaskClass = (
  connectionsConfiguration,
  inputDevice,
  dock,
  pressProfile
) => {
  const masks = [];
  const inputDirectionFilter = connection =>
    connection.targetDockNumber === dock.number &&
    connection.sourceDockNumber ===
      inputDevice.connectionInputConfig.sourceDockNumber &&
    connection.sourceDeviceNumber ===
      inputDevice.connectionInputConfig.sourceDeviceNumber &&
    connection.triggerEvent === pressProfile;
  const outputDirectionFilter = connection =>
    connection.sourceDockNumber === dock.number &&
    connection.targetDockNumber ===
      inputDevice.connectionInputConfig.targetDockNumber &&
    connection.targetDeviceNumber ===
      inputDevice.connectionInputConfig.targetDeviceNumber &&
    connection.triggerEvent === pressProfile;

  if (!inputDevice) {
    return masks;
  }

  /**
   * For start column only one mask is visible and is highlighted
   */
  masks.push(
    [
      ...calculateDeviceMasks(
        inputDevice.connectionInputConfig,
        dock,
        inputDevice.startsFrom
      ),
      'device-mask--in-edit'
    ].join(' ')
  );

  /**
   * Makes visible multiple masks for devices from different column then which starts from (output / input)
   */
  connectionsConfiguration
    .filter(
      inputDevice.startsFrom === 'input'
        ? inputDirectionFilter
        : outputDirectionFilter
    )
    .forEach(({ connection }) => {
      masks.push(
        calculateDeviceMasks(
          connection,
          dock,
          inputDevice.startsFrom === 'input' ? 'output' : 'input'
        ).join(' ')
      );
    });

  return masks;
};

export const onDeviceItemClick = (
  dock: Dock,
  device: Device,
  configDirection: string,
  inputDevice: IInputDevice,
  setInputDevice: React.Dispatch<React.SetStateAction<IInputDevice>>,
  pressProfile: string,
  connectionsConfiguration,
  updateConnections,
  isPreviewMode: boolean = false
) => {
  const { symbol } = device;
  const deviceIndex = getDeviceIndex(dock.devices, device);
  const targetAction = Object.keys(device.actions).shift();
  const deviceAction = deviceActions.find(({ key }) => key === targetAction);
  const deviceConnectionId = composeDeviceConnectionId(
    deviceIndex,
    dock.number,
    configDirection
  );

  /**
   * clicking on same device item which is currently active
   * result: deactivate current active device
   * */
  if (inputDevice && inputDevice.id === deviceConnectionId) {
    setInputDevice(null);
    /**
     * clicking different device item then currently active but from same column (input / output column)
     * result: setting new device as active
     * */
  } else if (
    isPreviewMode ||
    !inputDevice ||
    (inputDevice && inputDevice.startsFrom === configDirection)
  ) {
    const connectionInputConfig =
      configDirection === 'input'
        ? {
            sourceDeviceNumber: deviceIndex,
            sourceDockNumber: dock.number,
            triggerEvent: pressProfile,
            triggerEventParameters: {}
          }
        : // Configuration for output
          {
            targetAction,
            targetActionParameters: deviceAction
              ? deviceAction.value()
              : ({} as IColor),
            targetDeviceNumber: deviceIndex,
            targetDockNumber: dock.number,
            triggerEvent: pressProfile
          };

    setInputDevice({
      id: deviceConnectionId,
      startsFrom: configDirection,
      symbol,
      connectionInputConfig
    });
    /**
     * clicking on device item from other column which is currently active and adding new connection
     * result: adding new connection
     * */
  } else {
    let connectionOutputConfig =
      configDirection === 'input'
        ? {
            sourceDeviceNumber: deviceIndex,
            sourceDockNumber: dock.number,
            triggerEventParameters: {}
          }
        : {
            targetAction,
            targetDeviceNumber: deviceIndex,
            targetDockNumber: dock.number,
            targetActionParameters: deviceAction ? deviceAction.value() : {}
          };
    // Checking if the output is Scene
    if (inputDevice.connectionInputConfig.targetAction === SCENE_ACTION) {
      // @ts-ignore
      connectionOutputConfig = {
        targetDeviceNumber: deviceIndex,
        targetDockNumber: dock.number,
        sourceDeviceNumber: deviceIndex,
        sourceDockNumber: dock.number,
        triggerEventParameters: {}
      };
    }

    const newConnection = new DevicesConnection({
      ...inputDevice.connectionInputConfig,
      ...connectionOutputConfig
    });
    const getConnectionIndex = (): number => {
      if (inputDevice.connectionInputConfig.targetAction === SCENE_ACTION) {
        return connectionsConfiguration.findIndex(
          ({ connection }) =>
            connection.targetActionParameters.SceneId === inputDevice.id &&
            connection.triggerEvent === pressProfile &&
            connection.targetDeviceNumber === deviceIndex &&
            connection.targetDockNumber === dock.number
        );
      }
      return connectionsConfiguration.findIndex(
        ({ connection }) =>
          connection.triggerEvent ===
            inputDevice.connectionInputConfig.triggerEvent &&
          (configDirection === 'input'
            ? connection.targetDeviceNumber ===
                inputDevice.connectionInputConfig.targetDeviceNumber &&
              connection.targetDockNumber ===
                inputDevice.connectionInputConfig.targetDockNumber &&
              connection.sourceDockNumber === dock.number &&
              connection.sourceDeviceNumber === deviceIndex
            : connection.targetAction === targetAction &&
              connection.targetDeviceNumber === deviceIndex &&
              connection.targetDockNumber === dock.number &&
              connection.sourceDockNumber ===
                inputDevice.connectionInputConfig.sourceDockNumber &&
              connection.sourceDeviceNumber ===
                inputDevice.connectionInputConfig.sourceDeviceNumber)
      );
    };

    const connectionIndex = getConnectionIndex();
    if (connectionIndex < 0) {
      updateConnections([...connectionsConfiguration, newConnection]);
    } else {
      connectionsConfiguration.splice(connectionIndex, 1);
      updateConnections([...connectionsConfiguration]);
    }
  }
};

export const composeSceneConnectionId = (
  inputDevice: IInputDevice,
  sceneId: string
) => `scene_${sceneId}_${inputDevice.id}`;

// Logic on scene click
export const onSceneItemClick = (
  scene: Scene,
  inputDevice: IInputDevice,
  connectionsConfiguration,
  updateConnections,
  setInputDevice: React.Dispatch<React.SetStateAction<IInputDevice>>,
  _configDirection: string,
  pressProfile: string,
  isPreviewMode: boolean = false
) => {
  if (inputDevice && inputDevice.id === scene.id) {
    setInputDevice(null);
    /**
     * clicking different device item then currently active but from same column (input / output column)
     * result: setting new device as active
     * */
  } else if (
    isPreviewMode ||
    !inputDevice ||
    (inputDevice.id !== scene.id && inputDevice.startsFrom !== 'input')
  ) {
    const connectionInputConfig = {
      targetAction: SCENE_ACTION,
      targetActionParameters: { SceneId: scene.id },
      triggerEvent: pressProfile,
      targetDeviceNumber: null,
      targetDockNumber: null
    };
    // @ts-ignore
    setInputDevice({
      id: scene.id,
      startsFrom: 'output',
      connectionInputConfig
    });
  } else {
    const connectionOutputConfig = {
      sourceDeviceNumber: inputDevice.connectionInputConfig.sourceDeviceNumber,
      sourceDockNumber: inputDevice.connectionInputConfig.sourceDockNumber,
      targetDeviceNumber: inputDevice.connectionInputConfig.sourceDeviceNumber,
      targetDockNumber: inputDevice.connectionInputConfig.sourceDockNumber,
      targetAction: 'ActionInvokeScene',
      targetActionParameters: { SceneId: scene.id }
    };

    const newConnection = new DevicesConnection({
      ...inputDevice.connectionInputConfig,
      ...connectionOutputConfig
    });

    // Checking if input has another scene connection on the particular press profile
    const anotherSceneConectionIndex = connectionsConfiguration.findIndex(
      ({ connection }) =>
        inputDevice.startsFrom === 'input' &&
        connection.sourceDockNumber ===
          inputDevice.connectionInputConfig.sourceDockNumber &&
        connection.sourceDeviceNumber ===
          inputDevice.connectionInputConfig.sourceDeviceNumber &&
        connection.targetAction === SCENE_ACTION &&
        connection.targetActionParameters.SceneId !== scene.id &&
        connection.triggerEvent === pressProfile
    );

    const connectionIndex = connectionsConfiguration.findIndex(
      ({ connection }) =>
        inputDevice.startsFrom === 'output'
          ? connection.targetAction === SCENE_ACTION &&
            connection.targetActionParameters.SceneId === scene.id
          : connection.triggerEvent ===
              inputDevice.connectionInputConfig.triggerEvent &&
            connection.targetDeviceNumber ===
              inputDevice.connectionInputConfig.sourceDeviceNumber &&
            connection.targetDockNumber ===
              inputDevice.connectionInputConfig.sourceDockNumber &&
            connection.targetActionParameters.SceneId === scene.id
    );

    if (connectionIndex < 0 || anotherSceneConectionIndex >= 0) {
      if (anotherSceneConectionIndex >= 0) {
        connectionsConfiguration.splice(anotherSceneConectionIndex, 1);
      }
      updateConnections([...connectionsConfiguration, newConnection]);
    } else {
      connectionsConfiguration.splice(connectionIndex, 1);
      updateConnections([...connectionsConfiguration]);
    }
  }
};

// Filter arrows ones some component exists
export const connectionArrowsFilterFunction = (
  inputDevice: IInputDevice,
  pressProfile: PressProfiles,
  connection: ConnectionConfig
) => {
  if (!inputDevice) {
    return false;
  }

  const outputDeviceCondition = conn => {
    if (conn.targetAction === SCENE_ACTION) {
      return inputDevice.id === connection.targetActionParameters.SceneId;
    }
    return (
      connection.triggerEvent === pressProfile &&
      inputDevice.connectionInputConfig.targetDockNumber ===
        connection.targetDockNumber &&
      inputDevice.connectionInputConfig.targetDeviceNumber ===
        connection.targetDeviceNumber
    );
  };

  const sceneConnectionFilter = conn =>
    conn.targetActionParameters.SceneId &&
    connection.triggerEvent === pressProfile &&
    conn.targetAction === SCENE_ACTION;

  const inputDeviceCondition =
    connection.triggerEvent === pressProfile &&
    inputDevice.connectionInputConfig.sourceDockNumber ===
      connection.sourceDockNumber &&
    inputDevice.connectionInputConfig.sourceDeviceNumber ===
      connection.sourceDeviceNumber;
  const deviceDirectionCondition =
    inputDevice.startsFrom === 'input'
      ? inputDeviceCondition
      : outputDeviceCondition(connection);
  return (
    deviceDirectionCondition &&
    isElementPresent(
      composeDeviceConnectionId(
        connection.sourceDeviceNumber,
        connection.sourceDockNumber,
        'input'
      )
    ) &&
    (isElementPresent(
      composeDeviceConnectionId(
        connection.targetDeviceNumber,
        connection.targetDockNumber,
        'output'
      )
    ) ||
      sceneConnectionFilter(connection))
  );
};

export const isDeviceDisabled = (
  device,
  inputDevice,
  configurationDirection
): boolean => {
  const isSmokeDetector = device.symbol === deviceTypes.smokeDetector.symbol;

  // Smoke detectors should not be possible to change by user
  if (isSmokeDetector) {
    return true;
  }

  if (!inputDevice || inputDevice.startsFrom !== configurationDirection) {
    return false;
  }

  let frameButtonLedCheck;
  let standardDevicesCheck;

  // If start configuring from input column input device must be frame button and searching connection with frame led
  if (configurationDirection === 'input') {
    frameButtonLedCheck =
      inputDevice.symbol === deviceTypes.frameButton.symbol &&
      device.symbol !== deviceTypes.frameLed.symbol;
    standardDevicesCheck =
      device.symbol === deviceTypes.frameLed.symbol &&
      inputDevice.symbol !== deviceTypes.frameButton.symbol;
    // If start configuring from output column input device must be frame led and searching connection with frame button
  } else {
    frameButtonLedCheck =
      inputDevice.symbol === deviceTypes.frameLed.symbol &&
      device.symbol !== deviceTypes.frameButton.symbol;
    standardDevicesCheck =
      device.symbol === deviceTypes.frameButton.symbol &&
      inputDevice.symbol !== deviceTypes.frameLed.symbol;
  }

  return frameButtonLedCheck || standardDevicesCheck;
};

export const isDeviceGrayed = (
  previewMode,
  isActive,
  inputDevice,
  dock,
  device,
  configurationDirection
) => {
  if (previewMode && !isActive) {
    return true;
  }

  if (!inputDevice || inputDevice.startsFrom !== configurationDirection) {
    return false;
  }

  return (
    inputDevice.id !==
    `${configurationDirection}_${dock.number}_${getDeviceIndex(
      dock.devices,
      device
    )}`
  );
};

export const AnchorFromOutput: React.FC<{
  inputDevice: IInputDevice | null;
  pressProfile: string;
  refresh?: number;
}> = ({ inputDevice, pressProfile, refresh }) => {
  let startId;
  let endId;
  let title;
  if (inputDevice && inputDevice.startsFrom === 'output') {
    if (
      inputDevice.connectionInputConfig &&
      inputDevice.connectionInputConfig.targetAction !== SCENE_ACTION
    ) {
      startId = `output_${inputDevice.connectionInputConfig.targetDockNumber}_${inputDevice.connectionInputConfig.targetDeviceNumber}`;
      endId = `${pressProfile}${inputDevice.id}ProfileBtn`;
      title = `output_${inputDevice.connectionInputConfig.targetDockNumber}_${inputDevice.connectionInputConfig.targetDeviceNumber}_${refresh}`;
    } else {
      startId = `scene_${inputDevice.connectionInputConfig.targetActionParameters.SceneId}`;
      endId = `${pressProfile}${inputDevice.connectionInputConfig.targetActionParameters.SceneId}ProfileBtn`;
      title = `scene_${inputDevice.connectionInputConfig.targetActionParameters.SceneId}_${refresh}`;
    }
  } else {
    return <></>;
  }
  return (
    <Xarrow
      path="grid"
      startAnchor="left"
      endAnchor="right"
      start={startId}
      end={endId}
      key="outputDeviceToPressProfile"
      strokeWidth={2}
      headSize={4}
      // @ts-ignore
      title={title}
    />
  );
};

export const AnchorFromInput: React.FC<{
  inputDevice: IInputDevice | null;
  pressProfile: string;
  refresh?: number;
}> = ({ inputDevice, pressProfile, refresh }) => {
  return (
    <Xarrow
      path="grid"
      startAnchor="right"
      endAnchor="left"
      start={`input_${inputDevice.connectionInputConfig.sourceDockNumber}_${inputDevice.connectionInputConfig.sourceDeviceNumber}`}
      end={`${pressProfile}${inputDevice.id}ProfileBtn`}
      key="inputDeviceToPressProfile"
      strokeWidth={2}
      headSize={4}
      // @ts-ignore
      title={`input_${inputDevice.connectionInputConfig.sourceDockNumber}_${inputDevice.connectionInputConfig.sourceDeviceNumber}_${refresh}`}
    />
  );
};
